/** Opening evidence is deliberately three-state. Missing or unsupported hours
 * do not establish either an opening or a closure. All times are resort-local. */
export type VenueOpeningState = "open" | "closed" | "unknown";
export type GooglePeriodPoint = { day: number; hour: number; minute: number };
export type GoogleOpeningHours = {
  periods?: { open?: GooglePeriodPoint; close?: GooglePeriodPoint }[];
} | { source: "openstreetmap"; raw: string | null } | null;
type Interval = [number, number];
const WEEK = 7 * 24 * 60;
const object = (value: unknown): value is Record<string, unknown> => value !== null && typeof value === "object" && !Array.isArray(value);
const dayNames = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const pointMinute = (value: unknown): number | null => {
  if (!object(value) || !Number.isInteger(value.day) || Number(value.day) < 0 || Number(value.day) > 6
    || !Number.isInteger(value.hour) || Number(value.hour) < 0 || Number(value.hour) > 23
    || !Number.isInteger(value.minute) || Number(value.minute) < 0 || Number(value.minute) > 59) return null;
  return Number(value.day) * 1440 + Number(value.hour) * 60 + Number(value.minute);
};

/** Supports unambiguous weekly OSM hours only. Holidays, seasons, sunrise,
 * comments and conditional rules remain unknown rather than being guessed. */
function osmIntervals(raw: string): Interval[] | null {
  const text = raw.trim();
  if (text === "24/7") return [[0, WEEK]];
  if (text === "off" || text === "closed") return [];
  if (text.length > 1000) return null;
  const intervals: Interval[] = [];
  const assigned = new Set<number>();
  for (const rule of text.split(";")) {
    const match = rule.trim().match(/^((?:(?:Mo|Tu|We|Th|Fr|Sa|Su)(?:-(?:Mo|Tu|We|Th|Fr|Sa|Su))?)(?:,(?:Mo|Tu|We|Th|Fr|Sa|Su)(?:-(?:Mo|Tu|We|Th|Fr|Sa|Su))?)*)\s+(.+)$/);
    // A bare time range is an explicit daily opening, not a guessed weekday.
    const days = match ? match[1] : "Mo-Su";
    const times = match ? match[2] : rule.trim();
    const activeDays: number[] = [];
    for (const range of days.split(",")) {
      const [first, last = first] = range.split("-").map((value) => dayNames.indexOf(value));
      if (first < 0 || last < 0) return null;
      for (let day = first, count = 0; count < 7; count++, day = (day + 1) % 7) {
        activeDays.push(day);
        if (day === last) break;
      }
    }
    // Overlapping rule precedence is a richer part of the OSM grammar. Keep
    // it unknown instead of accidentally overriding an overnight exception.
    if (activeDays.some((day) => assigned.has(day))) return null;
    activeDays.forEach((day) => assigned.add(day));
    if (times === "off" || times === "closed") continue;
    for (const range of times.split(",")) {
      const time = range.trim().match(/^(\d{2}):(\d{2})-(\d{2}):(\d{2})$/);
      if (!time) return null;
      const [, oh, om, ch, cm] = time.map(Number);
      if (oh > 23 || om > 59 || ch > 24 || cm > 59 || ch === 24 && cm !== 0) return null;
      const start = oh * 60 + om, end = ch * 60 + cm;
      if (start === end) return null;
      for (const day of activeDays) intervals.push([day * 1440 + start, day * 1440 + end + (end < start ? 1440 : 0)]);
    }
  }
  return intervals;
}

function openingIntervals(hours: unknown): Interval[] | null {
  if (typeof hours === "string") return osmIntervals(hours);
  if (!object(hours)) return null;
  if (hours.source === "openstreetmap") return typeof hours.raw === "string" ? osmIntervals(hours.raw) : null;
  if (typeof hours.osm === "string") return osmIntervals(hours.osm);
  if (!Array.isArray(hours.periods) || hours.periods.length === 0 || hours.periods.length > 100) return null;
  const intervals: Interval[] = [];
  for (const period of hours.periods) {
    if (!object(period)) return null;
    const start = pointMinute(period.open);
    if (start === null) return null;
    // Google's documented sentinel represents every day, not only Sunday.
    if (period.close == null && start === 0 && hours.periods.length === 1) return [[0, WEEK]];
    const end = pointMinute(period.close);
    if (end === null || end === start) return null;
    const sameDay = Math.floor(start / 1440) === Math.floor(end / 1440);
    intervals.push([start, end + (end < start ? sameDay ? 1440 : WEEK : 0)]);
  }
  return intervals;
}

export function venueOpeningDuring(hours: unknown, weekday: number, startHour: number, endHour = startHour): VenueOpeningState {
  if (!Number.isInteger(weekday) || weekday < 0 || weekday > 6 || !Number.isFinite(startHour) || !Number.isFinite(endHour)
    || startHour < 0 || startHour >= 24 || endHour < startHour || endHour > 48) return "unknown";
  const intervals = openingIntervals(hours);
  if (intervals === null) return "unknown";
  const start = weekday * 1440 + startHour * 60, end = weekday * 1440 + endHour * 60;
  const ordered = intervals.flatMap(([open, close]) => [-WEEK, 0, WEEK].map((offset) => [open + offset, close + offset] as Interval)).sort((a, b) => a[0] - b[0]);
  let through = start;
  for (const [open, close] of ordered) {
    if (close <= through || open > through + 1e-8) continue;
    through = Math.max(through, close);
    if (through >= end - 1e-8) return "open";
  }
  return "closed";
}

export function resortWeekday(date: string): number | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return null;
  const parsed = new Date(`${date}T12:00:00Z`);
  if (!Number.isFinite(parsed.getTime()) || parsed.toISOString().slice(0, 10) !== date) return null;
  return parsed.getUTCDay();
}
